import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import type FetchService from '@triptyk/ember-utils/services/fetch';
import { format, parseISO } from 'date-fns';
import type HeadDataService from 'frontend/services/head-data';
import type IntlService from 'ember-intl/services/intl';

export type FormationsIndexRouteParams = Record<
  keyof FormationsIndexRoute['queryParams'],
  string | number | undefined
>;

export type FormationsIndexRouteModel = Resolved<
  ReturnType<FormationsIndexRoute['model']>
>;

export default class FormationsIndexRoute extends Route {
  @service declare store: StoreService;
  @service declare fetch: FetchService;
  @service declare headData: HeadDataService;
  @service declare intl: IntlService;

  queryParams = {
    targetAudience: {
      refreshModel: true,
      replace: true,
    },
    modality: {
      refreshModel: true,
      replace: true,
    },
    planned: {
      refreshModel: true,
      replace: true,
    },
    isNew: {
      refreshModel: true,
      replace: true,
    },
    parcours: {
      refreshModel: true,
      replace: true,
    },
    families: {
      refreshModel: true,
      replace: true,
    },
    tags: {
      refreshModel: true,
      replace: true,
    },
    start: {
      refreshModel: true,
      replace: true,
    },
    end: {
      refreshModel: true,
      replace: true,
    },
    q: {
      refreshModel: true,
      replace: true,
    },
    page: {
      refreshModel: true,
      replace: true,
    },
    size: {
      refreshModel: true,
      replace: true,
    },
  };

  async model(params: FormationsIndexRouteParams) {
    const queryParams = [
      this.createQueryParam(params, 'q'),
      this.createQueryParam(params, 'size', 20),
      this.createQueryParam(params, 'page', 1),
      this.createQueryParam(params, 'targetAudience'),
      this.createQueryParam(params, 'modality'),
      this.createQueryParam(params, 'families'),
      this.createQueryParam(params, 'planned'),
      this.createQueryParam(params, 'isNew'),
      this.createQueryParam(params, 'parcours'),
      this.createQueryParam(params, 'tags'),
      this.createQueryParamWithTransform(params, 'start', (value) =>
        format(parseISO(value as string), 'yyyy-MM-dd')
      ),
      this.createQueryParamWithTransform(params, 'end', (value) =>
        format(parseISO(value as string), 'yyyy-MM-dd')
      ),
    ]
      .filter((p) => p)
      .join('&');

    const [formationsResult, families, tags] = await Promise.all([
      this.fetch.request(`search?${queryParams}`, {}).then((r) => r.json()),
      this.store.query('family', {
        include: 'children,products',
      }),
      this.store.query('tag', {}),
    ]);

    return {
      formations: formationsResult.data,
      meta: formationsResult.meta,
      families,
      tags,
    };
  }

  beforeModel() {
    this.headData.title = this.intl.t('routes.formation.title');
    this.headData.description = this.intl.t('routes.formation.description');
    this.headData.keywords = this.intl.t('routes.formation.keywords');
    this.headData.noindex = false;
    this.headData.shouldStructuredData = true;
    this.headData.isStructuredDataCourse = false;
    this.headData.setUrl();
  }

  private createQueryParam(
    params: FormationsIndexRouteParams,
    key: keyof FormationsIndexRouteParams,
    defaultValue?: string | number
  ) {
    if (!params[key]) {
      return defaultValue ? `${key}=${defaultValue}` : '';
    }
    return `${key}=${params[key]}`;
  }

  private createQueryParamWithTransform(
    params: FormationsIndexRouteParams,
    key: keyof FormationsIndexRouteParams,
    transform: (value: string | number) => string | number
  ) {
    if (!params[key]) {
      return '';
    }
    return `${key}=${transform(params[key] as string | number)}`;
  }
}
